import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faTrash,
  faPlus,
  faSpinner,
  faCheckCircle,
  faExclamationCircle,
  faTimes,
  faList,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";

export default function SchemeFeatureManagement() {
  const [schemeFeatures, setSchemeFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);
  const [formData, setFormData] = useState({
    featureName: "",
    schemeFeatureDescriptionKey: "",
    category: "",
    schemeFeatureDefinition: "",
    schemeDescriptions: {},
    panelMemberPanelItem: {
      PanelMemberPanelItemCode: "",
      PanelMemberPanelItemLines: [],
    },
  });
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSchemeFeatures();
  }, []);

  const fetchSchemeFeatures = async () => {
    try {
      const response = await fetch("/api/policies/all", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch scheme features");
      }
      const data = await response.json();
      setSchemeFeatures(data.AllSchemeFeatureDescriptions);
    } catch (error) {
      setError({
        type: "error",
        message: "Error loading scheme features",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCategoryChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      category: e.target.value,
    }));
  };

  const handleSchemeDescriptionChange = (key, index, value) => {
    setFormData((prev) => {
      const newSchemeDescriptions = { ...prev.schemeDescriptions };
      if (!newSchemeDescriptions[key]) {
        newSchemeDescriptions[key] = [];
      }
      newSchemeDescriptions[key][index] = value;
      return {
        ...prev,
        schemeDescriptions: newSchemeDescriptions,
      };
    });
  };

  const addSchemeDescription = (key) => {
    setFormData((prev) => {
      const newSchemeDescriptions = { ...prev.schemeDescriptions };
      if (!newSchemeDescriptions[key]) {
        newSchemeDescriptions[key] = [];
      }
      newSchemeDescriptions[key].push("");
      return {
        ...prev,
        schemeDescriptions: newSchemeDescriptions,
      };
    });
  };

  const removeSchemeDescription = (key, index) => {
    setFormData((prev) => {
      const newSchemeDescriptions = { ...prev.schemeDescriptions };
      newSchemeDescriptions[key].splice(index, 1);
      if (newSchemeDescriptions[key].length === 0) {
        delete newSchemeDescriptions[key];
      }
      return {
        ...prev,
        schemeDescriptions: newSchemeDescriptions,
      };
    });
  };

  const handlePanelItemLineChange = (index, value) => {
    setFormData((prev) => {
      const newLines = [...prev.panelMemberPanelItem.PanelMemberPanelItemLines];
      newLines[index] = {
        ...newLines[index],
        PanelItemLine: value,
      };
      return {
        ...prev,
        panelMemberPanelItem: {
          ...prev.panelMemberPanelItem,
          PanelMemberPanelItemLines: newLines,
        },
      };
    });
  };

  const addPanelItemLine = () => {
    setFormData((prev) => ({
      ...prev,
      panelMemberPanelItem: {
        ...prev.panelMemberPanelItem,
        PanelMemberPanelItemLines: [
          ...prev.panelMemberPanelItem.PanelMemberPanelItemLines,
          { PanelItemLine: "", PanelItemLineFormat: "" },
        ],
      },
    }));
  };

  const removePanelItemLine = (index) => {
    setFormData((prev) => {
      const newLines = [...prev.panelMemberPanelItem.PanelMemberPanelItemLines];
      newLines.splice(index, 1);
      return {
        ...prev,
        panelMemberPanelItem: {
          ...prev.panelMemberPanelItem,
          PanelMemberPanelItemLines: newLines,
        },
      };
    });
  };

  const openModal = (feature = null) => {
    if (feature) {
      setFormData({
        featureName: feature.featureName || "",
        schemeFeatureDescriptionKey: feature.schemeFeatureDescriptionKey,
        category: feature.category,
        schemeFeatureDefinition: feature.schemeFeatureDefinition,
        schemeDescriptions: feature.schemeDescriptions || {},
        panelMemberPanelItem: feature.panelMemberPanelItem || {
          PanelMemberPanelItemCode: "",
          PanelMemberPanelItemLines: [],
        },
      });
      setEditingFeature(feature);
    } else {
      setFormData({
        featureName: "",
        schemeFeatureDescriptionKey: "",
        category: "",
        schemeFeatureDefinition: "",
        schemeDescriptions: {},
        panelMemberPanelItem: {
          PanelMemberPanelItemCode: "",
          PanelMemberPanelItemLines: [],
        },
      });
      setEditingFeature(null);
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingFeature(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setActionLoading(true);

    try {
      const response = await fetch(
        editingFeature
          ? `/api/scheme-description/${editingFeature.schemeFeatureDescriptionKey}`
          : "/api/scheme-description",
        {
          method: editingFeature ? "PUT" : "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...formData,
            featureName: formData.featureName.trim(),
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to save scheme feature");
      }

      await fetchSchemeFeatures();
      closeModal();
      setError({
        type: "success",
        message: `Scheme feature ${
          editingFeature ? "updated" : "created"
        } successfully`,
      });
    } catch (error) {
      setError({
        type: "error",
        message: error.message,
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this scheme feature? This will also delete all related descriptions and panel items."
      )
    ) {
      return;
    }

    setActionLoading(true);
    try {
      // First, delete all scheme descriptions
      const descriptionsResponse = await fetch(
        `/api/scheme-features/delete-descriptions/panel-members/${id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!descriptionsResponse.ok) {
        throw new Error("Failed to delete scheme descriptions");
      }

      await fetchSchemeFeatures();
      setError({
        type: "success",
        message: "Scheme feature and all related data deleted successfully",
      });
    } catch (error) {
      setError({
        type: "error",
        message:
          error.message === "Failed to fetch"
            ? "Network error. Please check your connection."
            : error.message,
      });
      console.error("Error deleting scheme feature:", error);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
        <p>Loading scheme features...</p>
      </div>
    );
  }

  return (
    <div className="scheme-feature-management">
      {error && (
        <div className={`message-box ${error.type}`}>
          <span>
            <FontAwesomeIcon
              icon={
                error.type === "success" ? faCheckCircle : faExclamationCircle
              }
            />
            {error.message}
          </span>
          <button onClick={() => setError(null)}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}

      <div className="management-header">
        <h2>Scheme Feature Management</h2>
        <button
          className="add-btn"
          onClick={() => navigate("/admin/dashboard/scheme-features/new")}
          disabled={actionLoading}
        >
          <FontAwesomeIcon icon={faPlus} />
          <span className="btn-text">Add Scheme Feature</span>
        </button>
      </div>

      <div className="management-grid">
        {schemeFeatures.map((feature) => (
          <div
            key={feature.schemeFeatureDescriptionKey}
            className="management-card"
          >
            <div className="card-content">
              <h3>{feature.featureName || "Unnamed Feature"}</h3>
              <p className="feature-key">
                {feature.schemeFeatureDescriptionKey}
              </p>
              <p className="category-tag">{feature.category}</p>
              <p className="feature-definition">
                {feature.schemeFeatureDefinition}
              </p>

              {Object.entries(feature.schemeDescriptions || {}).map(
                ([key, descriptions]) => (
                  <div key={key} className="descriptions-section">
                    <h4>{key}</h4>
                    <ul>
                      {descriptions.map((desc, index) => (
                        <li key={index}>{desc}</li>
                      ))}
                    </ul>
                  </div>
                )
              )}

              {feature.panelMemberPanelItem && (
                <div className="panel-item-section">
                  <h4>
                    <FontAwesomeIcon icon={faList} /> Panel Item Lines
                  </h4>
                  {feature.panelMemberPanelItem.PanelMemberPanelItemLines?.map(
                    (line, index) => (
                      <div key={index} className="line-item">
                        <span>{line.PanelItemLine}</span>
                        {line.PanelItemLineFormat && (
                          <small>({line.PanelItemLineFormat})</small>
                        )}
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
            <div className="card-actions">
              <button
                className="edit-btn"
                onClick={() =>
                  navigate(
                    `/admin/dashboard/scheme-features/edit/${feature.id}`
                  )
                }
                disabled={actionLoading}
                title="Edit scheme feature"
              >
                <FontAwesomeIcon icon={faEdit} />
              </button>
              <button
                className="delete-btn"
                onClick={() => handleDelete(feature.id)}
                disabled={actionLoading}
                title="Delete scheme feature"
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>
              {editingFeature ? "Edit Scheme Feature" : "Add Scheme Feature"}
            </h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="featureName">Feature Name</label>
                <input
                  type="text"
                  id="featureName"
                  name="featureName"
                  value={formData.featureName}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter a descriptive name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="schemeFeatureDescriptionKey">Feature Key</label>
                <input
                  type="text"
                  id="schemeFeatureDescriptionKey"
                  name="schemeFeatureDescriptionKey"
                  value={formData.schemeFeatureDescriptionKey}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="category">Category</label>
                <select
                  id="category"
                  value={formData.category}
                  onChange={handleCategoryChange}
                  required
                >
                  <option value="">Select Category</option>
                  <option value="ImportantTerm">Important Term</option>
                  <option value="QuoteFeature">Quote Feature</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="schemeFeatureDefinition">Definition</label>
                <textarea
                  id="schemeFeatureDefinition"
                  name="schemeFeatureDefinition"
                  value={formData.schemeFeatureDefinition}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Scheme Descriptions</label>
                {Object.entries(formData.schemeDescriptions).map(
                  ([key, descriptions]) => (
                    <div key={key} className="description-group">
                      <h4>{key}</h4>
                      {descriptions.map((desc, index) => (
                        <div key={index} className="description-item">
                          <input
                            type="text"
                            value={desc}
                            onChange={(e) =>
                              handleSchemeDescriptionChange(
                                key,
                                index,
                                e.target.value
                              )
                            }
                          />
                          <button
                            type="button"
                            onClick={() => removeSchemeDescription(key, index)}
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => addSchemeDescription(key)}
                        className="add-description-btn"
                      >
                        <FontAwesomeIcon icon={faPlus} /> Add Description
                      </button>
                    </div>
                  )
                )}
              </div>

              <div className="form-group">
                <label>Panel Member Panel Item</label>
                <input
                  type="text"
                  placeholder="Panel Item Code"
                  value={formData.panelMemberPanelItem.PanelMemberPanelItemCode}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      panelMemberPanelItem: {
                        ...prev.panelMemberPanelItem,
                        PanelMemberPanelItemCode: e.target.value,
                      },
                    }))
                  }
                />

                <div className="panel-lines">
                  <button
                    type="button"
                    onClick={addPanelItemLine}
                    className="add-line-btn"
                  >
                    <FontAwesomeIcon icon={faPlus} /> Add Line
                  </button>
                  {formData.panelMemberPanelItem.PanelMemberPanelItemLines.map(
                    (line, index) => (
                      <div key={index} className="line-form">
                        <input
                          type="text"
                          placeholder="Panel Line"
                          value={line.PanelItemLine}
                          onChange={(e) =>
                            handlePanelItemLineChange(index, e.target.value)
                          }
                        />
                        <button
                          type="button"
                          className="remove-line-btn"
                          onClick={() => removePanelItemLine(index)}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </div>
                    )
                  )}
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" disabled={actionLoading}>
                  {actionLoading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin />{" "}
                      {editingFeature ? "Updating..." : "Creating..."}
                    </>
                  ) : editingFeature ? (
                    "Update"
                  ) : (
                    "Create"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style jsx>{`
        .feature-key {
          color: #666;
          font-size: 0.9rem;
          margin: 0.5rem 0;
          font-family: monospace;
        }

        .management-card h3 {
          margin: 0;
          font-size: 1.2rem;
          color: #000;
        }
      `}</style>
    </div>
  );
}
